import { Icon, Button as IndigoButton } from "@indigo-components";
import { FC, type RefObject, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import type { ChatAnnotation } from "~/pages/appointments/hooks/useAgentChat";
import { annotationFromSelection } from "./annotationSelection";

/** Gap between the selection and the button, and the room above the
 *  selection below which the button flips underneath it instead. */
const OFFSET_PX = 8;
const FLIP_BELOW_PX = 48;

interface Anchor {
  annotation: ChatAnnotation;
  top: number;
  left: number;
  below: boolean;
}

interface AddSelectionToChatProps {
  /** The message list's scroll container; selections are read inside it. */
  containerRef: RefObject<HTMLElement>;
  onAdd: (annotation: ChatAnnotation) => void;
}

/**
 * Floating "Add to chat" over a text selection inside one message. Shown on
 * mouse/key release anywhere on the page (a drag can end outside the list)
 * so it doesn't chase a drag; follows the selection when the list scrolls —
 * a streaming reply scrolls it every token — and hides once the selection
 * collapses or scrolls out of view.
 */
export const AddSelectionToChat: FC<AddSelectionToChatProps> = ({
  containerRef,
  onAdd,
}) => {
  const [anchor, setAnchor] = useState<Anchor | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const locate = (): Anchor | null => {
      const selection = window.getSelection();
      const annotation = annotationFromSelection(selection, container);
      if (!annotation || !selection) return null;
      const rect = selection.getRangeAt(0).getBoundingClientRect();
      const below = rect.top < FLIP_BELOW_PX;
      return {
        annotation,
        top: below ? rect.bottom + OFFSET_PX : rect.top - OFFSET_PX,
        left: rect.left + rect.width / 2,
        below,
      };
    };
    const show = () => setAnchor(locate());
    const selectionInView = () => {
      const selection = window.getSelection();
      // Cleared since the last selectionchange: nothing left to follow.
      if (!selection?.rangeCount) return false;
      const rect = selection.getRangeAt(0).getBoundingClientRect();
      const view = container.getBoundingClientRect();
      return rect.bottom >= view.top && rect.top <= view.bottom;
    };
    // Only moves a button already shown, so a scroll mid-drag shows nothing.
    const follow = () =>
      setAnchor((prev) => (prev && selectionInView() ? locate() : null));
    const hideIfCleared = () => {
      if (!annotationFromSelection(window.getSelection(), container))
        setAnchor(null);
    };
    document.addEventListener("mouseup", show);
    document.addEventListener("keyup", show);
    container.addEventListener("scroll", follow, { passive: true });
    document.addEventListener("selectionchange", hideIfCleared);
    return () => {
      document.removeEventListener("mouseup", show);
      document.removeEventListener("keyup", show);
      container.removeEventListener("scroll", follow);
      document.removeEventListener("selectionchange", hideIfCleared);
    };
  }, [containerRef]);

  if (!anchor) return null;

  // Portalled and fixed: the Indy drawer clips and transforms its body, and
  // the selection's rect is already in viewport coordinates.
  return createPortal(
    <div
      className="indy-add-selection fixed z-ant-popover"
      style={{
        top: anchor.top,
        left: anchor.left,
        transform: `translate(-50%, ${anchor.below ? "0" : "-100%"})`,
      }}
    >
      <IndigoButton
        variant="secondary"
        size="sm"
        type="button"
        icon={<Icon name="comment" size="s" />}
        className="shadow-indigo-sm"
        // Keep the selection alive through the click.
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => {
          onAdd(anchor.annotation);
          window.getSelection()?.removeAllRanges();
          setAnchor(null);
        }}
      >
        Add to chat
      </IndigoButton>
    </div>,
    document.body,
  );
};
